import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

declare global {
  interface Window {
    fbq: (...args: any[]) => void;
    _fbq: any;
  }
}

// Helper to save events to database
const saveEventToDatabase = async (
  eventName: string,
  parameters?: Record<string, any>,
  userId?: string
) => {
  try {
    await supabase.from('meta_pixel_events').insert({
      event_name: eventName,
      page_url: window.location.href,
      content_name: parameters?.content_name,
      content_category: parameters?.content_category,
      value: parameters?.value,
      currency: parameters?.currency || 'EUR',
      user_id: userId || null,
      session_id: sessionStorage.getItem('session_id') || crypto.randomUUID(),
      metadata: parameters || {}
    });
  } catch (error) {
    console.error('Error saving Meta Pixel event:', error);
  }
};

export const MetaPixel = () => {
  const location = useLocation();
  const { user } = useAuth();
  const PIXEL_ID = "2308047109717495";

  // Generate or retrieve session ID
  useEffect(() => {
    if (!sessionStorage.getItem('session_id')) {
      sessionStorage.setItem('session_id', crypto.randomUUID());
    }
  }, []);

  useEffect(() => {
    // Defer Meta Pixel initialization until page is interactive
    const initMetaPixel = () => {
      // Inizializzazione Meta Pixel
      (function(f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
        if (f.fbq) return;
        n = f.fbq = function() {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

      window.fbq('init', PIXEL_ID);
      window.fbq('track', 'PageView');
    };

    // Wait for page to be interactive before loading Meta Pixel
    if (document.readyState === 'complete') {
      setTimeout(initMetaPixel, 800);
    } else {
      window.addEventListener('load', () => {
        setTimeout(initMetaPixel, 800);
      });
    }
  }, []);

  // Track PageView ad ogni cambio route
  useEffect(() => {
    if (window.fbq) {
      window.fbq('track', 'PageView');
      saveEventToDatabase('PageView', { page_url: window.location.href }, user?.id);
    }
  }, [location, user]);

  return null;
};

// Funzioni helper per eventi personalizzati
export const trackMetaEvent = async (eventName: string, parameters?: Record<string, any>) => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', eventName, parameters);
    
    // Save to database
    const { data: { user } } = await supabase.auth.getUser();
    await saveEventToDatabase(eventName, parameters, user?.id);
  }
};

// Eventi standard più comuni
export const trackViewContent = (contentName: string, contentCategory?: string) => {
  trackMetaEvent('ViewContent', {
    content_name: contentName,
    content_category: contentCategory,
  });
};

export const trackContact = () => {
  trackMetaEvent('Contact');
};

export const trackLead = (value?: number, currency?: string) => {
  trackMetaEvent('Lead', {
    value: value,
    currency: currency || 'EUR',
  });
};

export const trackCompleteRegistration = (method?: string) => {
  trackMetaEvent('CompleteRegistration', {
    content_name: method || 'email',
  });
};

export const trackInitiateCheckout = (value?: number, currency?: string) => {
  trackMetaEvent('InitiateCheckout', {
    value: value,
    currency: currency || 'EUR',
  });
};

export const trackPurchase = (value: number, currency?: string) => {
  trackMetaEvent('Purchase', {
    value: value,
    currency: currency || 'EUR',
  });
};

export default MetaPixel;
